import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { DataManager } from '../../core/data-manager';
import { LoginManager } from '../../core/login-manager';
import { PalazzineManager } from '../../lib/palazzine-manager';
import { refId } from '../../lib/format';
import { isAdminGlobale } from '../../lib/ruoli';
import {
    useEntityList, useEntityItem, statoLista, Field, DetailPage, DetailActions,
    ConfirmModal, messaggioErrore
} from './common';
import Select from '../../components/Select';
import AppartamentiPalazzina from './appartamenti';

const VUOTA = { nome: '', indirizzo: '', note: '', idUserGroup: 0 };

/**
 * Pagina di una palazzina, con dentro i suoi appartamenti.
 *
 * Il condominio di appartenenza lo sceglie solo l'amministratore globale, ed
 * e' l'unico campo che non viaggia con il resto del form: la CRUD del
 * framework non accetta `idUserGroup`, lo timbra da se' con il gruppo di chi
 * scrive. Ci pensa `assegna_palazzina` subito dopo il salvataggio, e con la
 * palazzina si portano dietro i suoi appartamenti.
 */
export default function Palazzina() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [userInfo] = useState(() => LoginManager.getUserInfo());
    const globale = isAdminGlobale(userInfo);

    const nuova = !id || id === 'nuova';
    const { item, loading, error, reload } = useEntityItem('palazzina', nuova ? null : id);
    const appartamenti = useEntityList('appartamento', 'identificativo', 'asc');
    // solo l'amministratore globale sceglie: gli altri hanno un condominio solo
    const condomini = useEntityList(globale ? 'user_group' : null, 'name', 'asc');

    const [form, setForm] = useState(nuova ? { ...VUOTA } : null);
    const [saving, setSaving] = useState(false);
    const [daEliminare, setDaEliminare] = useState(false);
    const [working, setWorking] = useState(false);

    useEffect(() => {
        if (!item) return;
        setForm({
            nome: item.nome || '',
            indirizzo: item.indirizzo || '',
            note: item.note || '',
            idUserGroup: refId(item.idUserGroup)
        });
    }, [item]);

    function aggiorna(campo, valore) {
        setForm(prev => ({ ...prev, [campo]: valore }));
    }

    async function salva(e) {
        e.preventDefault();
        if (!form.nome.trim()) {
            toast.error('Il nome della palazzina è obbligatorio.', { theme: 'colored' });
            return;
        }
        if (globale && !parseInt(form.idUserGroup, 10)) {
            toast.error('Scegli il condominio a cui appartiene la palazzina.', { theme: 'colored' });
            return;
        }

        const payload = {
            nome: form.nome.trim(),
            indirizzo: form.indirizzo,
            note: form.note
        };

        setSaving(true);
        try {
            const res = nuova
                ? await DataManager.insertItem('palazzina', payload)
                : await DataManager.updateItem('palazzina', payload, item.id);

            if (!res || res.success !== 1) {
                toast.error(messaggioErrore(res, 'Salvataggio non riuscito.'), { theme: 'colored' });
                return;
            }

            // la create risponde con l'id in cima, non dentro `body`
            const idPalazzina = nuova ? parseInt(res.id, 10) : item.id;

            /* Il condominio e' un secondo giro: la CRUD ha appena timbrato la
               palazzina con il gruppo di chi scrive, che per l'amministratore
               globale non e' mai quello giusto. */
            const scelto = parseInt(form.idUserGroup, 10) || 0;
            const attuale = nuova ? 0 : refId(item.idUserGroup);
            let assegnata = true;

            if (globale && idPalazzina && scelto && scelto !== attuale) {
                const esito = await PalazzineManager.assegnaPalazzina(idPalazzina, scelto);
                assegnata = Boolean(esito && esito.success === 1);
                if (!assegnata) {
                    toast.error(
                        messaggioErrore(esito, 'Palazzina salvata, ma non assegnata al condominio.'),
                        { theme: 'colored' }
                    );
                }
                else if (!nuova) reload();
            }

            if (assegnata) {
                toast.success(nuova ? 'Palazzina creata.' : 'Palazzina aggiornata.', { theme: 'colored' });
            }
            // appena creata si resta dentro: mancano gli appartamenti
            if (nuova) navigate(idPalazzina ? `/admin/anagrafiche/${idPalazzina}` : '/admin/anagrafiche');
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setSaving(false);
        }
    }

    async function elimina() {
        setWorking(true);
        try {
            const res = await DataManager.deleteItem('palazzina', item.id);
            if (res && res.success === 1) {
                toast.success('Palazzina eliminata.', { theme: 'colored' });
                navigate('/admin/anagrafiche');
            }
            else {
                toast.error(messaggioErrore(res), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setWorking(false);
        }
    }

    /**
     * Un appartamento creato dall'amministratore globale nasce nel gruppo di
     * chi lo scrive, non in quello della palazzina che lo contiene: la stessa
     * chiamata dell'assegnazione lo rimette al suo posto.
     */
    async function appartamentiCambiati() {
        const gruppo = item ? refId(item.idUserGroup) : 0;
        if (globale && gruppo) {
            const esito = await PalazzineManager.assegnaPalazzina(item.id, gruppo);
            if (!esito || esito.success !== 1) {
                toast.error(
                    messaggioErrore(esito, 'Appartamento salvato, ma non assegnato al condominio.'),
                    { theme: 'colored' }
                );
            }
        }
        appartamenti.reload();
    }

    const stato = statoLista({ loading: loading, error: error, empty: !form, icon: 'building' });

    return (
        <DetailPage
            backTo="/admin/anagrafiche"
            backLabel="Torna alle palazzine"
            eyebrow="Anagrafica immobiliare"
            title={nuova ? 'Nuova palazzina' : (item ? item.nome : 'Palazzina')}
        >
            {stato || (
                <>
                    <form onSubmit={salva}>
                        <div className="lv-form-grid">
                            {globale && (
                                <Field
                                    label="Condominio"
                                    required
                                    hint="Chi vede la palazzina e i suoi appartamenti. Si cambia finché non ci abita nessuno."
                                >
                                    <Select
                                        value={form.idUserGroup}
                                        onChange={v => aggiorna('idUserGroup', v)}
                                        options={[
                                            { value: '0', label: 'Scegli il condominio…' },
                                            ...condomini.items.map(g => ({ value: String(g.id), label: g.name }))
                                        ]}
                                    />
                                </Field>
                            )}
                            <Field label="Nome" required hint="Es. «Palazzina A».">
                                <input className="lv-input" value={form.nome} onChange={e => aggiorna('nome', e.target.value)} />
                            </Field>
                            <Field label="Indirizzo">
                                <input
                                    className="lv-input"
                                    value={form.indirizzo}
                                    onChange={e => aggiorna('indirizzo', e.target.value)}
                                />
                            </Field>
                            <Field label="Note" wide>
                                <textarea
                                    className="lv-input"
                                    rows={2}
                                    value={form.note}
                                    onChange={e => aggiorna('note', e.target.value)}
                                />
                            </Field>
                        </div>

                        <DetailActions saving={saving} submitLabel={nuova ? 'Crea palazzina' : 'Salva modifiche'}>
                            {!nuova && (
                                <button
                                    type="button"
                                    className="lv-btn lv-btn-ghost lv-btn-danger-ghost"
                                    onClick={() => setDaEliminare(true)}
                                >
                                    <FontAwesomeIcon icon={['fas', 'trash']} className="me-2" />
                                    Elimina
                                </button>
                            )}
                        </DetailActions>
                    </form>

                    {nuova ? (
                        <div className="lv-subsection">
                            <div className="lv-note">
                                Gli appartamenti si aggiungono dopo aver creato la palazzina.
                            </div>
                        </div>
                    ) : (
                        <AppartamentiPalazzina
                            palazzina={item}
                            appartamenti={appartamenti.items.filter(a => refId(a.idPalazzina) === item.id)}
                            onChanged={appartamentiCambiati}
                        />
                    )}
                </>
            )}

            <ConfirmModal
                show={daEliminare}
                title="Eliminare la palazzina?"
                message={item
                    ? `«${item.nome}» sparirà dall'anagrafica. I suoi appartamenti restano, ma senza palazzina.`
                    : ''}
                confirmLabel="Elimina"
                danger
                working={working}
                onConfirm={elimina}
                onClose={() => setDaEliminare(false)}
            />
        </DetailPage>
    );
}
